import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { billingApi, billingQueryKeys } from "../../../shared/api/billing";
import { formatVndWithUnit } from "../../../shared/format";
import { useOperatesModule } from "../../../shared/hooks/useOperatesModule";
import type {
  DiscountType,
  InvoiceItemInput,
  InvoiceItemKind,
  VatCategory,
} from "../../../shared/types/billing";

interface Props {
  items: InvoiceItemInput[];
  onChange: (next: InvoiceItemInput[]) => void;
}

const VAT_OPTIONS: VatCategory[] = ["KCT", "VAT_0", "VAT_5", "VAT_8", "VAT_10"];

const nativeSelectClass =
  "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

function lineGross(it: InvoiceItemInput): number {
  return it.quantity * it.unitPrice;
}

function lineDiscountAmt(it: InvoiceItemInput): number {
  if (!it.discountType || it.discountValue == null) return 0;
  if (it.discountType === "PERCENT") {
    const pct = Math.max(0, Math.min(100, it.discountValue));
    return Math.round(lineGross(it) * (pct / 100));
  }
  return Math.max(0, Math.min(lineGross(it), it.discountValue));
}

function lineTotal(it: InvoiceItemInput): number {
  return Math.round(lineGross(it) - lineDiscountAmt(it));
}

export function InvoiceItemsEditor({ items, onChange }: Props) {
  const services = useQuery({
    queryKey: billingQueryKeys.services(),
    queryFn: () => billingApi.listServices(),
  });
  // Hoá đơn là màn hình gói nào cũng có (ADR-0053), nhưng danh mục thuốc thuộc vùng Đơn thuốc —
  // phòng khám không kê đơn trên Addy, hay gói không có Đơn thuốc, thì ô "+ Thuốc" không có gì
  // để chọn và lượt gọi danh mục sẽ nhận 409/402. Chờ `loaded`: vẽ ô này là gọi API.
  const prescribing = useOperatesModule("PRESCRIPTIONS");
  const usesMedicines = prescribing.loaded && prescribing.operates;
  const medicines = useQuery({
    queryKey: billingQueryKeys.medicines(),
    queryFn: () => billingApi.listMedicines(),
    enabled: usesMedicines,
  });

  function update(idx: number, patch: Partial<InvoiceItemInput>) {
    onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  function remove(idx: number) {
    onChange(items.filter((_, i) => i !== idx));
  }

  function addService(serviceId: string) {
    const svc = services.data?.find((s) => s.id === serviceId);
    if (!svc) return;
    onChange([
      ...items,
      {
        kind: "SERVICE",
        refId: svc.id,
        name: svc.name,
        unit: svc.unit,
        quantity: 1,
        unitPrice: svc.priceVnd,
        discountType: null,
        discountValue: null,
        vatCategory: svc.vatCategory,
        notes: null,
      },
    ]);
  }

  function addMedicine(medicineId: string) {
    const m = medicines.data?.find((x) => x.id === medicineId);
    if (!m) return;
    onChange([
      ...items,
      {
        kind: "MEDICINE",
        refId: m.id,
        name: m.name,
        unit: m.unit,
        quantity: 1,
        unitPrice: m.sellPrice,
        discountType: null,
        discountValue: null,
        vatCategory: m.vatCategory,
        notes: null,
      },
    ]);
  }

  function addCustom() {
    onChange([
      ...items,
      {
        kind: "CUSTOM",
        refId: null,
        name: "",
        unit: null,
        quantity: 1,
        unitPrice: 0,
        discountType: null,
        discountValue: null,
        vatCategory: "KCT",
        notes: null,
      },
    ]);
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[70px]">Loại</TableHead>
              <TableHead>Tên dịch vụ / thuốc</TableHead>
              <TableHead className="w-[70px]">ĐVT</TableHead>
              <TableHead className="w-[80px]">SL</TableHead>
              <TableHead className="w-[130px]">Đơn giá</TableHead>
              <TableHead className="w-[130px]">Giảm</TableHead>
              <TableHead className="w-[90px]">VAT</TableHead>
              <TableHead className="w-[140px] text-right">Thành tiền</TableHead>
              <TableHead className="w-[40px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="py-6 text-center text-muted-foreground">
                  Chưa có mục nào. Thêm dịch vụ, thuốc hoặc dòng tự nhập bên dưới.
                </TableCell>
              </TableRow>
            )}
            {items.map((it, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  <KindBadge kind={it.kind} />
                </TableCell>
                <TableCell>
                  {it.kind === "CUSTOM" ? (
                    <Input
                      type="text"
                      value={it.name}
                      placeholder="Tên dịch vụ / vật tư…"
                      onChange={(e) => update(idx, { name: e.target.value })}
                    />
                  ) : (
                    <div className="font-semibold">{it.name}</div>
                  )}
                </TableCell>
                <TableCell>
                  {it.kind === "CUSTOM" ? (
                    <Input
                      type="text"
                      value={it.unit ?? ""}
                      onChange={(e) => update(idx, { unit: e.target.value || null })}
                    />
                  ) : (
                    <span className="font-mono text-sm text-muted-foreground">
                      {it.unit ?? "—"}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    value={it.quantity}
                    onChange={(e) =>
                      update(idx, { quantity: Math.max(1, Number(e.target.value)) })
                    }
                    className="text-right"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    min={0}
                    step={1000}
                    value={it.unitPrice}
                    onChange={(e) =>
                      update(idx, { unitPrice: Math.max(0, Number(e.target.value)) })
                    }
                    className="text-right"
                  />
                </TableCell>
                <TableCell>
                  <DiscountInput
                    type={it.discountType ?? null}
                    value={it.discountValue ?? null}
                    onChange={(type, value) =>
                      update(idx, { discountType: type, discountValue: value })
                    }
                  />
                </TableCell>
                <TableCell>
                  <select
                    className={nativeSelectClass}
                    value={it.vatCategory}
                    onChange={(e) =>
                      update(idx, { vatCategory: e.target.value as VatCategory })
                    }
                  >
                    {VAT_OPTIONS.map((v) => (
                      <option key={v} value={v}>
                        {v === "KCT" ? "KCT" : v.replace("VAT_", "") + "%"}
                      </option>
                    ))}
                  </select>
                </TableCell>
                <TableCell className="text-right">
                  <strong>{formatVndWithUnit(lineTotal(it))}</strong>
                </TableCell>
                <TableCell>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    aria-label="Xoá dòng"
                    onClick={() => remove(idx)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label>+ Dịch vụ</Label>
          <select
            className={nativeSelectClass}
            value=""
            onChange={(e) => {
              if (e.target.value) addService(e.target.value);
              e.target.value = "";
            }}
          >
            <option value="">— Chọn dịch vụ —</option>
            {(services.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} · {formatVndWithUnit(s.priceVnd)}
              </option>
            ))}
          </select>
        </div>
        {usesMedicines && (
          <div className="space-y-1.5">
            <Label>+ Thuốc</Label>
            <select
              className={nativeSelectClass}
              value=""
              onChange={(e) => {
                if (e.target.value) addMedicine(e.target.value);
                e.target.value = "";
              }}
            >
              <option value="">— Chọn thuốc —</option>
              {(medicines.data ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} · {formatVndWithUnit(m.sellPrice)}/{m.unit}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="space-y-1.5">
          <Label>&nbsp;</Label>
          <Button type="button" variant="secondary" className="w-full" onClick={addCustom}>
            + Dòng tự nhập
          </Button>
        </div>
      </div>
    </div>
  );
}

function KindBadge({ kind }: { kind: InvoiceItemKind }) {
  const label = kind === "SERVICE" ? "Dịch vụ" : kind === "MEDICINE" ? "Thuốc" : "Khác";
  const variant = kind === "SERVICE" ? "secondary" : kind === "MEDICINE" ? "success" : "muted";
  return <Badge variant={variant}>{label}</Badge>;
}

function DiscountInput({
  type,
  value,
  onChange,
}: {
  type: DiscountType | null;
  value: number | null;
  onChange: (t: DiscountType | null, v: number | null) => void;
}) {
  const enabled = type !== null;
  return (
    <div className="flex items-center gap-1.5">
      <Input
        type="number"
        min={0}
        value={value ?? ""}
        disabled={!enabled && type === null}
        onChange={(e) => {
          const v = e.target.value === "" ? null : Math.max(0, Number(e.target.value));
          onChange(type ?? "AMOUNT", v);
        }}
        placeholder="0"
        className="text-right"
      />
      <select
        className={cn(nativeSelectClass, "w-[52px] px-2")}
        value={type ?? "_"}
        onChange={(e) => {
          const next = e.target.value;
          if (next === "_") onChange(null, null);
          else onChange(next as DiscountType, value);
        }}
      >
        <option value="_">—</option>
        <option value="PERCENT">%</option>
        <option value="AMOUNT">đ</option>
      </select>
    </div>
  );
}
